import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
import type { Tone } from "@/types/tone";
import { Icon, type IconName } from "../icon";

const chipVariants = cva(
  [
    "inline-flex",
    "items-center",
    "justify-center",
    "max-w-full",
    "gap-(--spacing-4)",
    "h-(--spacing-24)",
    "rounded-(--radius-sm)",
    "overflow-hidden",
    "font-(family-name:--font-family-label)",
    "text-(length:--font-size-label-medium)",
    "leading-(--font-line-height-20)",
    "font-(--font-weight-medium)",
    "whitespace-nowrap",
  ],
  {
    variants: {
      tone: {
        positive: ["bg-chip-positive-container", "text-chip-positive-label"],
        neutral: ["bg-chip-neutral-container", "text-chip-neutral-label"],
        negative: ["bg-chip-negative-container", "text-chip-negative-label"],
        warning: ["bg-chip-warning-container", "text-chip-warning-label"],
        informative: [
          "bg-chip-informative-container",
          "text-chip-informative-label",
        ],
        brand: ["bg-chip-brand-container", "text-chip-brand-label"],
      },
      iconOnly: {
        true: ["px-(--spacing-4)", "py-(--spacing-4)"],
        false: ["px-(--spacing-6)", "py-(--spacing-4)"],
      },
    },
    defaultVariants: {
      tone: "neutral",
      iconOnly: false,
    },
  },
);

const iconToneMap = {
  informative: "text-chip-informative-label-icon",
  positive: "text-chip-positive-label-icon",
  warning: "text-chip-warning-label-icon",
  negative: "text-chip-negative-label-icon",
} as const;

type BaseTone =
  | Extract<
      Tone,
      "neutral" | "brand" | "positive" | "warning" | "negative" | "informative"
    >
  | null
  | undefined;

interface BaseChipContainerProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "className" | "color"> {
  tone?: BaseTone;
  icon?: IconName;
  label?: string | number;
  children?: React.ReactNode;
  truncate?: boolean;
}

function ChipContainer({
  tone,
  icon,
  label,
  children,
  truncate = true,
  ...props
}: BaseChipContainerProps) {
  const iconOnly = icon !== undefined && label === undefined;
  return (
    <span className={chipVariants({ tone, iconOnly })} {...props}>
      {icon && (
        <span
          className={cn(
            "inline-flex shrink-0 items-center",
            iconToneMap[tone as keyof typeof iconToneMap],
          )}
        >
          <Icon name={icon} size="xs" tone="inherit" />
        </span>
      )}
      {label !== undefined && (
        <span className={truncate ? "min-w-0 flex-1 truncate" : undefined}>
          {String(label)}
        </span>
      )}
      {children}
    </span>
  );
}

// Require at least one of icon or label
type ChipProps = Omit<BaseChipContainerProps, "children"> &
  (
    | { label: string | number; icon?: IconName }
    | { label?: string | number; icon: IconName }
  );

/**
 * Chip component for displaying labels, tags, or status indicators.
 *
 * Tones: positive, neutral, negative, warning, informative, brand
 *
 * Requires at least one of `label` or `icon`.
 */
function Chip(props: ChipProps) {
  return <ChipContainer {...props} />;
}

// ---- FilterChip ----

interface FilterChipProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "className" | "color"> {
  label: string | number;
  onClose: () => void;
  removeAriaLabel?: string;
}

/**
 * FilterChip is used exclusively in selects and filtering contexts.
 * Always neutral tone, no leading icon, always has a trailing close button.
 */
function FilterChip({
  label,
  onClose,
  removeAriaLabel,
  ...props
}: FilterChipProps) {
  return (
    <ChipContainer tone="neutral" label={label} {...props}>
      <button
        type="button"
        aria-label={removeAriaLabel ?? `Remove ${label}`}
        className="inline-flex shrink-0 cursor-pointer items-center text-icon-main rounded-(--radius-xs) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-offset-surface focus-visible:ring-button-focus-ring"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        <Icon name="x-close" size="xs" tone="inherit" />
      </button>
    </ChipContainer>
  );
}

export { Chip, type ChipProps, FilterChip, type FilterChipProps };
